import { useState, type ChangeEvent, type FocusEvent } from 'react';

import { BRL } from '@/shared/lib/format';
import { cruDoValor, editarDinheiro, mascararDinheiro } from '@/shared/lib/moneyInput';

import { Field, type FieldProps } from './Field';

export interface MoneyFieldProps extends Omit<
  FieldProps,
  'value' | 'onChange' | 'type' | 'leading'
> {
  /** Texto do formulario, o mesmo que `parseMoney` e `moneyInputError` leem. */
  value: string;
  onValueChange: (texto: string) => void;
  currency?: string | undefined;
}

/**
 * Campo de dinheiro: o `Field` com a mascara de reais.
 *
 * A pessoa digita e o campo mostra "R$ 400.000"; ao sair, completa os centavos:
 * "R$ 400.000,00". O formulario continua com o texto sem mascara, entao validacao,
 * mensagens e o valor enviado ao servidor sao os de sempre (ver shared/lib/moneyInput).
 *
 * Sair do campo so muda o que ele mostra: `onValueChange` chama apenas quando o texto
 * muda. Abrir um registro e sair dos campos nao altera nada, e um campo do perfil de
 * credito nao passa a contar como digitado so por ter recebido o foco.
 */
export function MoneyField({
  value,
  onValueChange,
  currency = BRL,
  placeholder,
  inputMode = 'decimal',
  onBlur,
  ...rest
}: MoneyFieldProps) {
  const [campo, setCampo] = useState(() => ({
    valor: value,
    cru: cruDoValor(value, currency),
  }));

  // O formulario trocou o valor por conta propria (reabriu, limpou, leu do servidor).
  let atual = campo;
  if (campo.valor !== value) {
    atual = { valor: value, cru: cruDoValor(value, currency) };
    setCampo(atual);
  }

  function aoDigitar(event: ChangeEvent<HTMLInputElement>) {
    const elemento = event.currentTarget;
    const { inputType } = event.nativeEvent as InputEvent;
    const edicao = editarDinheiro(
      atual.cru,
      elemento.value,
      elemento.selectionStart ?? elemento.value.length,
      { paraFrente: inputType === 'deleteContentForward', currency },
    );
    // Escreve aqui, e nao depois da pintura: o React recoloca o valor no campo ao fim do
    // evento, e o cursor iria para o fim a cada digito no meio do texto.
    elemento.value = edicao.exibido;
    elemento.setSelectionRange(edicao.cursor, edicao.cursor);
    // So o cursor andou (passou por um ponto ou pela virgula): nao ha o que avisar.
    if (edicao.cru === atual.cru) return;
    setCampo({ valor: edicao.valor, cru: edicao.cru });
    if (edicao.valor !== value) onValueChange(edicao.valor);
  }

  function aoSair(event: FocusEvent<HTMLInputElement>) {
    setCampo({ valor: value, cru: cruDoValor(value, currency) });
    onBlur?.(event);
  }

  return (
    <Field
      {...rest}
      inputMode={inputMode}
      placeholder={placeholder ?? mascararDinheiro('0,00', currency)}
      value={mascararDinheiro(atual.cru, currency)}
      onChange={aoDigitar}
      onBlur={aoSair}
    />
  );
}
